import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { get } from 'lodash';
import { PARTNERS } from '../../templates/FsuCorporatePartner/partners';
import SEO_PAGE_TYPES from './consts';
import getImageHelper from './_utils';

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        defaultDescription: description
        siteUrl: url
        defaultImage: image
      }
    }
  }
`;

const SEO = ({
  allData,
  listAsArticle = false,
  children = () => { },
  description = null,
  excludeFromSearchEngineIndexing = false,
  pageType = null,
  title = null
}) => {
  // Deconstruct props object passed directly to SEO from Head:
  const {
    data: pageData,
    location: { pathname },
    // As CSU pages are built programmatically inside the application the details
    // we need here are passed as part of 'context' object; see gatsby-node.js),
    pageContext: { partner: csuPartnerName }
  } = allData;

  // Generic fallbacks, used when this content (or type) doesn't have any to use:
  const siteData = useStaticQuery(query);
  const {
    site: {
      siteMetadata: {
        defaultTitle, defaultDescription, siteUrl, defaultImage
      }
    }
  } = siteData;

  let thisPageData;
  let thisTitle;
  let thisImage;
  let thisArticleDisplayDate;
  let thisDescription;
  let theseArticleEdges = [];
  let thesePrizeEdges;
  let thisCsuPartnerName;
  let landingSearchEngineExclude;

  // Longwinded, but necessary, given that there's very little consistency across page types:
  switch (pageType) {
    case SEO_PAGE_TYPES.LANDING:
      thisPageData = get(pageData, 'allContentfulPageLandingPage.edges[0].node');
      thisImage = getImageHelper(thisPageData, 'imageSeo') ?? `${siteUrl}/assets/news-default.jpg`;
      thisDescription = get(thisPageData, 'summary.summary');
      thisTitle = get(thisPageData, 'title');
      landingSearchEngineExclude = get(thisPageData, 'excludeFromSearchEngineIndexing');
      theseArticleEdges = get(pageData, 'allContentfulPageArticle.edges') ?? [];
      break;

    case SEO_PAGE_TYPES.ARTICLE:
    case SEO_PAGE_TYPES.PRESS_RELEASE:
      thisPageData = get(pageData, 'article.edges[0].node');
      thisImage = getImageHelper(thisPageData, 'image');
      thisDescription = get(thisPageData, 'summary.summary');
      thisTitle = get(thisPageData, 'title');
      thisArticleDisplayDate = get(thisPageData, 'displayDate');
      break;

    // The same 'page', just filtered or not:
    case SEO_PAGE_TYPES.ARTICLES:
    case SEO_PAGE_TYPES.ARTICLES_CATEGORY:
    case SEO_PAGE_TYPES.PRESS_RELEASES:
      thisPageData = get(pageData, 'allContentfulPageLandingPage.edges[0].node');
      thisDescription = get(thisPageData, 'summary.summary');
      theseArticleEdges = get(pageData, 'allContentfulPageArticle.edges') ?? [];
      break;

    case SEO_PAGE_TYPES.DEFAULT:
      thisPageData = get(pageData, 'allContentfulPageDefault.edges[0].node');
      thisTitle = get(thisPageData, 'title');
      break;

    case SEO_PAGE_TYPES.PARTNER:
      thisPageData = get(pageData, 'partner.edges[0].node');
      thisImage = getImageHelper(thisPageData, 'logo');
      thisDescription = get(thisPageData, 'summary.summary');
      thisTitle = get(thisPageData, 'name');
      break;

    case SEO_PAGE_TYPES.PERSON:
      thisPageData = get(pageData, 'allContentfulPagePerson.edges[0].node');
      thisImage = getImageHelper(thisPageData, 'image');
      thisDescription = get(thisPageData, 'summary.summary');
      thisTitle = get(thisPageData, 'title');
      break;

    case SEO_PAGE_TYPES.PRIZE:
      thisPageData = get(pageData, 'prize.edges[0].node');
      thisImage = getImageHelper(thisPageData, 'featuredImage');
      thisTitle = get(thisPageData, 'title');
      break;

    case SEO_PAGE_TYPES.PRIZES:
      thesePrizeEdges = get(pageData, 'allContentfulPrizes.edges');
      break;

    case SEO_PAGE_TYPES.FSU:
      thisPageData = get(pageData, 'contentfulPageFundraiserSignup2022');
      thisImage = getImageHelper(thisPageData, 'imageSeo');
      thisDescription = get(thisPageData, 'summary.summary');
      thisTitle = get(thisPageData, 'title');
      break;

    case SEO_PAGE_TYPES.CSU:
      // No page data available for these programatically-built pages:
      thisCsuPartnerName = PARTNERS[csuPartnerName].label;
      thisTitle = `${thisCsuPartnerName} fundraiser registration`;
      thisDescription = `${thisCsuPartnerName} fundraiser registration Comic Relief`;
      break;

    // No customisation needed, just adding for completeness:
    case SEO_PAGE_TYPES[404]:
    case SEO_PAGE_TYPES.SEARCH:
      break;

    default:
      // eslint-disable-next-line no-console
      console.error('UNKNOWN SEO PAGE TYPE');
  }

  // Use prop value first, then any assigned above, then fall back to a default:
  const finalTitle = title ?? thisTitle ?? defaultTitle;
  const finalDescription = description ?? thisDescription ?? defaultDescription;

  // No 'SEO' prop to cover this, so either use anything assigned above or just fall back:
  const finalImage = thisImage ?? defaultImage;

  const finalUrl = `${siteUrl}${pathname}`;

  const linkedDataObj = {
    // Only used by Homepage, Articles, News Categories and Press Releases page:
    theseArticleEdges,
    // Prizes only:
    thesePrizeEdges,
    // Article only
    articleDate: thisArticleDisplayDate,
    // Global:
    title: finalTitle,
    description: finalDescription,
    image: finalImage,
    url: finalUrl
  };

  /* Push current page to Datalayer */
  useEffect(() => {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    window.dataLayer.push({
      // Keep the previously-established property names:
      currentPageTitle: finalTitle,
      url: finalUrl
    });
  }, [finalTitle, finalUrl]);

  // Check Gatsby environment; production = true, anything else = false
  const isProd = process.env.GATSBY_ENVIRONMENT === 'production';

  // Keep this purely for the browser only, rather than seeping into linkedData:
  const thisTitleWithSuffix = `${linkedDataObj.title} | Comic Relief`;

  return (
    <>
      {/* Global */}
      <title>{thisTitleWithSuffix}</title>

      <meta name="description" content={linkedDataObj.description} />
      <meta name="image" content={linkedDataObj.image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@comicrelief" />

      {/* OG: context-based */}
      {linkedDataObj.url && <meta property="og:url" content={linkedDataObj.url} />}
      {linkedDataObj.title && <meta property="og:title" content={linkedDataObj.title} />}
      {linkedDataObj.description && <meta property="og:description" content={linkedDataObj.description} />}
      {linkedDataObj.image && <meta property="og:image" content={`${linkedDataObj.image}`} />}

      {/* Twitter: context-based */}
      {linkedDataObj.title && <meta name="twitter:title" content={linkedDataObj.title} />}
      {linkedDataObj.description && <meta name="twitter:description" content={linkedDataObj.description} />}
      {linkedDataObj.image && <meta name="twitter:image" content={`${linkedDataObj.image}`} />}

      {/* Used by Article, Default, Person and Press Release pages */}
      {listAsArticle && <meta property="og:type" content="article" />}

      {/* Exclude from search engine indexing if:
          - this Landing Page is excluded from search engine indexing in the CMS,
          - this 'SEO page type' explicitly has the excludeFromSearchEngineIndexing hardcoded, or
          - the environment is not production */}
      {(landingSearchEngineExclude || excludeFromSearchEngineIndexing || isProd === false) && <meta name="robots" content="noindex, nofollow" />}

      {/* Callback to attach linked data on Homepage only */}
      {children(siteUrl, linkedDataObj)}
    </>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  listAsArticle: PropTypes.bool,
  children: PropTypes.func,
  excludeFromSearchEngineIndexing: PropTypes.bool,
  allData: PropTypes.shape,
  pageType: PropTypes.string
};

export { SEO, SEO_PAGE_TYPES };
